import { useEffect, useSyncExternalStore } from "react";

import type { PageMessage, WorkerMessage } from "./sw-messages.js";

/**
 * The page's half of the offline story: install the worker, listen to the
 * three things it says, and let a screen reload itself when the cellar it is
 * showing turns out to have changed.
 *
 * It is a module with a variable in it rather than a context, on purpose. The
 * worker talks to the *page*, not to a component — there is one connection, it
 * exists before React mounts and outlives every unmount — and a provider
 * around that would be a ceremony with exactly one value in it.
 */

/**
 * Whether what is on screen came off the network or off the disk.
 *
 * `stale` is only ever set by a revalidation that *failed*. A cellar served
 * from the cache while the network answers behind it is not stale in any sense
 * worth a line on the screen — it is a fifth of a second old and about to be
 * confirmed — and a warning that appears on every ordinary load is a warning
 * nobody reads on the one load that matters.
 */
export type Freshness =
  { readonly status: "live" } | { readonly status: "stale"; readonly storedAt: Date };

const LIVE: Freshness = { status: "live" };

let freshness: Freshness = LIVE;
const watchers = new Set<() => void>();
const reloaders = new Set<() => void>();

function setFreshness(next: Freshness): void {
  // Compared rather than assigned, because `useSyncExternalStore` re-renders
  // on a new object whether or not it says anything new, and `live` arrives
  // after every single revalidation.
  if (next.status === "live" && freshness.status === "live") return;
  freshness = next;
  for (const watcher of watchers) watcher();
}

function watch(watcher: () => void): () => void {
  watchers.add(watcher);
  return () => {
    watchers.delete(watcher);
  };
}

/** What the interface asks: is this the cellar, or the cellar as it stood? */
export function useFreshness(): Freshness {
  return useSyncExternalStore(
    watch,
    () => freshness,
    () => LIVE,
  );
}

/**
 * Load now, and load again when the worker says the answer has changed.
 *
 * Every screen already had `useEffect(load, [load])`, and this is that line
 * with one addition. The second load is not a second request in any sense the
 * network can see: the worker has just written the fresh answer into the
 * cache, so the refetch is answered off the disk and the screen simply
 * redraws with a bottle somebody drank on another device.
 */
export function useReload(load: () => void): void {
  useEffect(load, [load]);
  useEffect(() => {
    reloaders.add(load);
    return () => {
      reloaders.delete(load);
    };
  }, [load]);
}

/**
 * Whether an unknown value that arrived on `postMessage` is one of ours.
 * Parsed rather than trusted, for the same reason `api.ts` parses every
 * response against its schema: it comes from outside this module's control,
 * and a `storedAt` that is not a string would become an `Invalid Date` on the
 * screen instead of an error at the boundary.
 *
 * On the page's side and not in `sw-messages.ts`, because that file must stay
 * types only — see the note there.
 */
export function isWorkerMessage(value: unknown): value is WorkerMessage {
  if (typeof value !== "object" || value === null || !("type" in value)) return false;
  const { type } = value as { type: unknown };
  if (type === "live" || type === "updated") return true;
  return (
    type === "stale" &&
    "storedAt" in value &&
    typeof (value as { storedAt: unknown }).storedAt === "string"
  );
}

/**
 * Start listening. Called once from `main.tsx`, before React mounts, so that
 * a page opened with no connection at all is told it is looking at the disk
 * by the very first read it makes.
 */
export function listenToTheWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  // Without this the worker talks to nobody, silently. A page holds its
  // messages from the worker until something asks for them, and assigning to
  // `onmessage` asks implicitly while `addEventListener` does not — so the
  // listener below is registered, is never called, and the interface quietly
  // stops saying that it is showing yesterday's cellar. Found by switching the
  // network off in Chromium and watching the line fail to appear, which is the
  // only way this class of mistake is ever found.
  navigator.serviceWorker.startMessages();

  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const message: unknown = event.data;
    if (!isWorkerMessage(message)) return;
    switch (message.type) {
      case "stale":
        setFreshness({ status: "stale", storedAt: new Date(message.storedAt) });
        return;
      case "live":
        setFreshness(LIVE);
        return;
      case "updated":
        setFreshness(LIVE);
        for (const reload of reloaders) reload();
        return;
    }
  });
}

/**
 * Install the worker.
 *
 * Production only, and that is not laziness about development: a worker
 * registered on `localhost:5173` serves Vite's dev bundle out of a cache and
 * turns every edit into a mystery. `npm run dev:pages` builds and serves the
 * real thing, which is where this is looked at.
 *
 * Nothing is done about a new worker taking over an open page. It claims the
 * page, the next navigation gets the new shell, and the alternative —
 * reloading under whoever is holding the phone — would throw away a
 * half-written tasting note to save them one tap.
 */
export function installTheWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    // A failed registration is swallowed on purpose. Everything this worker
    // does is an improvement on an application that already works without it,
    // so a browser that refuses it — an old one, a private window, a host that
    // served `sw.js` as the wrong type — should get the cellar and not an
    // error. The failure that would matter is the opposite one, a worker that
    // installs and misbehaves, and that is what `e2e/offline.spec.ts` walks.
    navigator.serviceWorker.register("/sw.js", { type: "module" }).catch(() => undefined);
  });
}

/**
 * Tell the worker the session is over, so the cellar stops being readable off
 * the disk by whoever holds the phone next.
 *
 * Told and not awaited, which is safe for one reason only: the worker takes
 * the deletion into `waitUntil`, so the browser keeps it alive until the
 * cache is gone even though this page has already moved on to the sign-in
 * screen. Without that this would be a race worth losing sleep over.
 */
export function forgetTheCellar(): void {
  if (!("serviceWorker" in navigator)) return;
  const worker = navigator.serviceWorker.controller;
  if (worker === null) return;
  const message: PageMessage = { type: "signed-out" };
  worker.postMessage(message);
  setFreshness(LIVE);
}
